import { actualPoints, benchPlayers, optimizeTeamWeek, startedPlayers } from "../optimizer.js";
import type { LeagueData, Player, Team, TeamWeek } from "../schemas.js";
import { isMatchupFinal, teamWeek } from "../standings.js";

/**
 * View-model helpers shared by the site's pages. Pure functions over
 * LeagueData (no DOM), so they're unit-testable.
 */

export interface Side {
  team: Team;
  tw: TeamWeek;
  actual: number;
  optimal: number;
  /** points left on the bench: optimal − actual */
  missed: number;
}

export interface Game {
  final: boolean;
  week: number;
  a: Side;
  b: Side;
  /** the best-lineup result differs from the actual result */
  flips: boolean;
}

function side(league: LeagueData, teamId: string, week: number): Side | null {
  const tw = teamWeek(league, teamId, week);
  const team = league.teams.find((t) => t.teamId === teamId);
  if (!tw || !team) return null;
  const actual = actualPoints(tw);
  const optimal = optimizeTeamWeek(tw, league).points;
  return { team, tw, actual, optimal, missed: optimal - actual };
}

export function gamesForWeek(league: LeagueData, week: number): Game[] {
  const games: Game[] = [];
  for (const m of league.matchups) {
    if (m.week !== week) continue;
    const a = side(league, m.teamA, week);
    const b = side(league, m.teamB, week);
    if (!a || !b) continue;
    const final = isMatchupFinal(m);
    const flips = final && Math.sign(a.actual - b.actual) !== Math.sign(a.optimal - b.optimal);
    games.push({ week, a, b, flips, final });
  }
  return games;
}

/** The game a team played in a week, oriented so `a` is that team. */
export function gameFor(league: LeagueData, week: number, teamId: string): Game | undefined {
  const game = gamesForWeek(league, week).find(
    (g) => g.a.team.teamId === teamId || g.b.team.teamId === teamId,
  );
  if (!game || game.a.team.teamId === teamId) return game;
  return { ...game, a: game.b, b: game.a };
}

/** Starting slots in league order, one entry per slot: QB, RB, RB, WR, ... */
export function slotList(league: LeagueData): string[] {
  return Object.entries(league.rosterSlots).flatMap(([slot, n]) => Array<string>(n).fill(slot));
}

export interface LineupRow {
  slot: string;
  player: Player | null;
}

export interface Lineup {
  starters: LineupRow[];
  /** everyone not in `starters` */
  bench: Player[];
  total: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Pull players into slots in league slot order; unmatched slots stay empty. */
function fillSlots(slots: string[], players: Player[], slotOf: (p: Player) => string): LineupRow[] {
  const pool = [...players];
  const rows = slots.map((slot) => {
    const i = pool.findIndex((p) => slotOf(p) === slot);
    return { slot, player: i >= 0 ? pool.splice(i, 1)[0]! : null };
  });
  // Starters in a slot the league settings don't list: show them rather than drop them.
  return rows.concat(pool.map((p) => ({ slot: slotOf(p), player: p })));
}

export function actualLineup(league: LeagueData, tw: TeamWeek): Lineup {
  const starters = fillSlots(slotList(league), startedPlayers(tw), (p) => p.selectedPosition);
  return { starters, bench: benchPlayers(tw), total: actualPoints(tw) };
}

export function optimalLineup(league: LeagueData, tw: TeamWeek): Lineup {
  const { points, lineup } = optimizeTeamWeek(tw, league);
  const slotOf = new Map(lineup.flatMap((r) => (r.player ? [[r.player, r.slot] as const] : [])));
  const picked = [...slotOf.keys()].sort((x, y) => y.points - x.points);
  const starters = fillSlots(slotList(league), picked, (p) => slotOf.get(p)!);
  const bench = tw.players.filter((p) => !slotOf.has(p)).sort((x, y) => y.points - x.points);
  return { starters, bench, total: points };
}
